<template>
  <b-form-group
    :label="panel.label"
    label-size="lg"
    data-test="panel-label"
  >
    <b-form-group
      label="Label:"
      label-cols="4"
      label-align="right"
      class="mb-0 pt-1"
    >
      <b-input v-model="label" />
    </b-form-group>

    <b-form-group
      label="Hide Border:"
      label-cols="4"
      label-align="right"
      class="mb-0"
    >
      <div class="h-100 d-flex align-items-center">
        <b-checkbox
          v-model="hideBorder"
          switch
        />
      </div>
    </b-form-group>

    <b-form-group
      label="Tooltip:"
      label-cols="4"
      label-align="right"
      class="mb-2 pt-1"
    >
      <b-textarea
        v-model="tooltip"
        rows="2"
        max-rows="2"
        data-test="tooltip"
      />
    </b-form-group>

    <b-form-group
      label="Link:"
      label-cols="4"
      label-align="right"
      class="mb-0 pt-1"
    >
      <b-select
        v-model="linkType"
        :options="linkTypes"
        data-test="link-type"
      />
    </b-form-group>

    <b-form-group
      v-if="displayUrlInput"
      label="Url:"
      label-align="top"
      class="mt-1 mb-0 pt-1"
    >
      <b-form-input
        v-model="$v.link.$model"
        type="url"
        :state="validateState($v.link)"
        data-test="url-input"
      />
    </b-form-group>

    <b-form-group
      v-if="displayStoryPicker"
      label="Select story to link to:"
      label-align="top"
      class="mt-1 mb-0 pt-1"
    >
      <ConsolidatedAppStoryPicker
        v-if="isConsolidatedApp"
        :selected-story-path.sync="link"
        data-test="consolidated-app-story-picker"
      />
      <StoryPicker
        v-else
        :selected-story-path.sync="link"
        data-test="story-picker"
      />
    </b-form-group>

    <TipTap
      class="mt-3"
      :content.sync="content"
      :style-settings="styleSettings"
      iframe-extension
      image-extension
      editor-height="250px"
    />

    <b-button
      class="mt-4"
      data-test="delete-content-panel"
      variant="danger"
      block
      @click="$emit('delete')"
    >
      Delete Content Panel
    </b-button>
  </b-form-group>
</template>

<script lang="ts">

import Vue, { PropType } from 'vue'
import Component from 'vue-class-component'
import { Validation } from 'vuelidate'
import { url } from 'vuelidate/lib/validators'

import TipTap from '@/components/TipTap/TipTap.vue'
import ConsolidatedAppStoryPicker from '@/components/builder/CustomVisualisationSettings/ConsolidatedAppStoryPicker.vue'
import StoryPicker from '@/components/builder/StoryEditor/StoryPicker.vue'
import { InfoCardContentPanel, InfoCardContentPanelKeys, InfoCardContentPanelLinkType, InfoCardContentPanelLinkTypes } from '@/types'

const debounce = require('lodash.debounce')

const components = {
  TipTap,
  ConsolidatedAppStoryPicker,
  StoryPicker,
}

const props = {
  panel: Object as PropType<InfoCardContentPanel>,
}

@Component({ components })
export default class InfoCardContentPanels extends Vue.extend({ props }) {
  debouncedContentChanged: any = null

  mounted () {
    this.debouncedContentChanged = debounce((newContent: string) => this.emitPropertyChanged(InfoCardContentPanelKeys.CONTENT, newContent), 1000)
  }

  unmounted () {
    if (this.debouncedContentChanged) {
      this.debouncedContentChanged.cancel()
    }
  }

  validations () {
    return {
      link: { url },
    }
  }

  validateState (state: Validation) {
    return (state.$dirty ? !state.$error : null) && null
  }

  get styleSettings () {
    return {
      fontSize: 100,
    }
  }

  get content () {
    return this.panel.content
  }

  set content (newContent) {
    this.debouncedContentChanged(newContent)
  }

  get label () {
    return this.panel.label
  }

  set label (newLabel: string) {
    this.emitPropertyChanged(InfoCardContentPanelKeys.LABEL, newLabel)
  }

  get tooltip () {
    return this.panel.tooltip ?? ''
  }

  set tooltip (value: string) {
    this.emitPropertyChanged(InfoCardContentPanelKeys.TOOLTIP, value)
  }

  linkTypes: { value: InfoCardContentPanelLinkType | null, text: string }[] = [
    { value: null, text: 'None' },
    { value: InfoCardContentPanelLinkTypes.EXTERNAL, text: 'URL' },
    { value: InfoCardContentPanelLinkTypes.INTERNAL, text: 'Internal' },
  ]

  get linkType () {
    return this.panel.linkType ?? null
  }

  set linkType (value: InfoCardContentPanelLinkType | null) {
    if (value !== this.panel.linkType) {
      this.emitPropertyChanged(InfoCardContentPanelKeys.LINK, undefined)
    }

    this.emitPropertyChanged(InfoCardContentPanelKeys.LINK_TYPE, value)
  }

  get link () {
    return this.panel.link
  }

  set link (path) {
    this.emitPropertyChanged(InfoCardContentPanelKeys.LINK, path)
  }

  get displayUrlInput () {
    return this.linkType === InfoCardContentPanelLinkTypes.EXTERNAL
  }

  get displayStoryPicker () {
    return this.linkType === InfoCardContentPanelLinkTypes.INTERNAL
  }

  get isConsolidatedApp () {
    return this.$typedStore.primary.app.isConsolidatedApp
  }

  get hideBorder () {
    return this.panel.hideBorder ?? false
  }

  set hideBorder (value: boolean) {
    this.emitPropertyChanged(InfoCardContentPanelKeys.HIDE_BORDER, value)
  }

  emitPropertyChanged (propertyName: string, newValue: any) {
    this.$emit('propertyChanged', propertyName, newValue)
  }
}

</script>
